import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import { PAGE_SIZE, getHot, searchContents } from '@/utils/content';

const TYPE_FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';

  const [keyword, setKeyword] = useState(q);
  const [activeType, setActiveType] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(q);
    setPage(1);
    setActiveType('all');
    document.title = q
      ? `搜索“${q}” - 中文字幕一区二区三区四虎在线`
      : '搜索影视 - 中文字幕一区二区三区四虎在线';
  }, [q]);

  const results = useMemo(() => searchContents(q), [q]);
  const filtered = useMemo(
    () => (activeType === 'all' ? results : results.filter((item) => item.category === activeType)),
    [results, activeType],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommended = getHot(6);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
    setPage(1);
  };

  const typeCount = (key: string) =>
    key === 'all' ? results.length : results.filter((item) => item.category === key).length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="text-sm text-foreground-500 mb-4" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <span className="mx-2">›</span>
        <span className="text-foreground-900">搜索</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">搜索影视资源</h1>
        <p className="mt-2 text-sm text-foreground-600">支持影视名称、题材、地区等关键词模糊匹配</p>
      </header>

      <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3 max-w-3xl">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            name="q"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入影视名称、题材或地区，如“科幻”“古装”"
            aria-label="搜索影视"
            className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {q ? (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <p className="text-sm text-foreground-700">
              关键词 “<span className="font-semibold text-primary-600">{q}</span>” 共找到{' '}
              <span className="font-semibold text-primary-600">{results.length}</span> 条结果
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {TYPE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => { setActiveType(filter.key); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  activeType === filter.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {filter.label}
                <span className="ml-1 text-xs opacity-80">({typeCount(filter.key)})</span>
              </button>
            ))}
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {pageItems.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-6">
              <EmptyState
                icon="ri-search-eye-line"
                title="暂无相关影视资源"
                description={`没有找到与“${q}”相关的内容，换个关键词试试，或看看下面的热门推荐。`}
                secondaryLabel="返回首页"
                secondaryTo="/"
              />
            </div>
          )}
        </>
      ) : (
        <div className="mt-6">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="试试搜索“科幻”“古装”“美食”“悬疑”等关键词，快速找到想看的中文字幕影视。"
            secondaryLabel="返回首页"
            secondaryTo="/"
          />
        </div>
      )}

      <section className="mt-12 md:mt-16">
        <div className="flex items-end justify-between gap-4 mb-5">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门推荐</h2>
          <Link to="/category/movie" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            查看更多
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {recommended.map((item) => (
            <MovieCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
}